<template>
  <div 
    class="confirmation-container d-flex align-items-center justify-content-center"
    :class="{ 'compact-view': asistenciaConfirmada }"
  >
    <div 
      class="confirmation-card p-4 p-md-5 rounded-4 shadow-lg"
      :class="{ 'compact-card': asistenciaConfirmada }"
      ref="formCardRef"
    >
      <!-- Vista de Confirmación Completa -->
      <div v-if="asistenciaConfirmada" class="qr-view text-center">
        <h3 class="confirmation-thanks">Thank you for confirming your attendance!</h3>
        <p class="form-subtitle mt-2">You will be redirected to WhatsApp to send your reply...</p>
      </div>

      <!-- Formulario en una sola pantalla -->
      <div v-else>
        <div class="form-header">
          <h2 ref="tituloAnimado" class="form-title">
            Confirm<br><span class="subtitulo">Attendance</span>
          </h2>
        </div>

        <form @submit.prevent="handleSubmit" class="confirmation-form">
          <div class="step-box">
            <!-- Nombre -->
            <div class="form-group">
              <label for="name" class="form-label">Name</label>
              <input
                id="name"
                v-model.trim="name"
                type="text"
                class="form-control"
                placeholder="Enter your full name"
                required
              >
            </div>

            <!-- Invitado por (Oculto si es evento XV o YM) -->
            <div v-if="!isXvEvent" class="form-group">
              <label class="form-label">Guest of?</label>
              <div class="radio-group">
                <div class="radio-option">
                  <input id="novia" v-model="invitedBy" type="radio" value="novia" class="radio-input" required>
                  <label for="novia" class="radio-label">Bride</label>
                </div>
                <div class="radio-option">
                  <input id="novio" v-model="invitedBy" type="radio" value="novio" class="radio-input">
                  <label for="novio" class="radio-label">Groom</label>
                </div>
              </div>
            </div>

            <!-- Número de invitados -->
            <div class="form-group">
              <label for="guests" class="form-label">How many are attending?</label>
              <select
                id="guests"
                v-model.number="guests"
                class="form-select"
                required
              >
                <option :value="null" disabled>Select an option</option>
                <option v-for="n in guestsOptions" :key="n" :value="n">{{ n }}</option>
              </select>
            </div>
          </div>

          <!-- Botón de Confirmación Directa -->
          <div class="mt-4">
            <button type="submit" class="submit-button" :disabled="isSubmitting">
              <span v-if="!isSubmitting">Confirm via WhatsApp</span>
              <span v-else class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            </button>
          </div>
          <div v-if="errorMessage" class="alert alert-danger mt-3">{{ errorMessage }}</div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed, nextTick } from 'vue';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export default {
  name: 'AttendanceConfirmation',
  props: {
    idInvitado: { type: Number, required: false, default: null },
    nombre: { type: String, default: '' },
    idFestejado: { type: Number, default: null }
  },
  setup(props) {
    const name = ref('');
    const invitedBy = ref('');
    const guests = ref(null);
    const guestsOptions = ref([1, 2, 3, 4, 5, 6]);
    const isSubmitting = ref(false);
    const errorMessage = ref('');
    const tituloAnimado = ref(null);
    const asistenciaConfirmada = ref(false);
    const formCardRef = ref(null);

    const isXvEvent = computed(() => {
      const eventName = props.nombre?.toLowerCase();
      return ['xv', 'ym'].includes(eventName);
    });

    const animateForm = () => {
      nextTick(() => {
        const box = formCardRef.value?.querySelector('.step-box');
        if (!box) return;
        gsap.fromTo(
          box,
          { y: 30, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.6, ease: 'power2.out' }
        );
      });
    };

    const handleSubmit = () => {
      if (!name.value.trim()) {
        errorMessage.value = 'Please enter your name.';
        return;
      }
      if (!isXvEvent.value && !invitedBy.value) {
        errorMessage.value = 'Please select who invited you.';
        return;
      }
      if (guests.value == null || Number.isNaN(Number(guests.value))) {
        errorMessage.value = 'Please select how many people will attend.';
        return;
      }

      isSubmitting.value = true;
      errorMessage.value = '';

      asistenciaConfirmada.value = true;

      // Generación del enlace directo de WhatsApp
      const targetPhone = '18433689754';
      let mensajeWhatsApp = `Hello! I confirm my attendance.%0A%0A` +
        `👤 *Name:* ${encodeURIComponent(name.value.trim())}%0A`;

      if (!isXvEvent.value && invitedBy.value) {
        const guestText = invitedBy.value === 'novia' ? 'Bride' : 'Groom';
        mensajeWhatsApp += `💌 *Guest of:* ${encodeURIComponent(guestText)}%0A`;
      }

      mensajeWhatsApp += `🎟️ *Guests:* ${guests.value}`;

      window.open(`https://api.whatsapp.com/send?phone=${targetPhone}&text=${mensajeWhatsApp}`, '_blank');
      isSubmitting.value = false;
    };

    onMounted(() => {
      animateForm();
      gsap.fromTo(
        tituloAnimado.value,
        { y: -150, opacity: 0, scale: 1.2 },
        {
          y: 0,
          opacity: 1,
          scale: 1,
          duration: 1.2,
          ease: 'bounce.out',
          scrollTrigger: {
            trigger: tituloAnimado.value,
            start: 'top 80%',
            toggleActions: 'restart none none reset'
          }
        }
      );
    });

    return {
      name, invitedBy, guests, guestsOptions,
      isSubmitting, errorMessage, isXvEvent, asistenciaConfirmada,
      handleSubmit, formCardRef, tituloAnimado
    };
  }
};
</script>

<style scoped>
/* Estilos base */
.confirmation-container {
  min-height: 70vh;
  padding: 2rem 1rem;
  transition: all 0.3s ease;
}

.confirmation-card {
  width: 100%;
  max-width: 500px;
  background: white;
  background: linear-gradient(to bottom, #ffffff, #f9f9f9);
  border: 1px solid #e0e0e0;
  transition: all 0.3s ease;
}

/* Vista compacta para confirmación */
.compact-view {
  min-height: 70vh;
  align-items: flex-start;
  padding-top: 4rem;
}

.compact-card {
  max-width: 400px;
  padding: 2rem;
}

/* Encabezado del formulario */
.form-header {
  margin-bottom: 2rem;
  text-align: center;
}

.form-title {
  font-family: 'british', cursive;
  font-size: 2rem;
  color: #f0c9d3;
  margin-bottom: 0.5rem;
}

.form-subtitle {
  font-family: 'Karla', sans-serif;
  color: #6c757d;
  font-size: 1rem;
}

/* Grupos de formulario */
.form-group {
  margin-bottom: 1.5rem;
}

.form-label {
  display: block;
  margin-bottom: 0.5rem;
  font-family: 'Karla', sans-serif;
  font-weight: 500;
  color: #495057;
}

/* Controles de formulario */
.form-control,
.form-select {
  width: 100%;
  padding: 0.75rem 1rem;
  font-size: 1rem;
  border: 1px solid #ced4da;
  border-radius: 0.5rem;
  background-color: rgba(255, 255, 255, 0.8);
  transition: border-color 0.3s, box-shadow 0.3s;
}

.form-control:focus,
.form-select:focus {
  border-color: #d592b0;
  box-shadow: 0 0 0 0.25rem rgba(213, 146, 176, 0.25);
  outline: none;
}

/* Radio buttons */
.radio-group {
  display: flex;
  gap: 1.5rem;
}

.radio-option {
  display: flex;
  align-items: center;
}

.radio-input {
  margin-right: 0.5rem;
  accent-color: #d592b0;
}

.radio-label {
  font-family: 'Karla', sans-serif;
  color: #495057;
}

/* Botón de enviar */
.submit-button {
  width: 100%;
  padding: 0.75rem;
  font-family: 'FeelingPassionate', cursive;
  font-size: 1.25rem;
  color: rgb(249, 249, 249);
  background-color: #f0c9d3;
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: background-color 0.3s;
}

.submit-button:hover {
  background-color: #c07d9b;
}

.submit-button:disabled {
  background-color: #d592b0;
  opacity: 0.7;
  cursor: not-allowed;
}

.confirmation-thanks {
  font-family: 'Karla', sans-serif;
  font-weight: 600;
  color: #354257;
  font-size: 1.25rem;
  margin-bottom: 1rem;
}

/* Mensajes de error */
.alert {
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
}

/* Responsive */
@media (max-width: 576px) {
  .confirmation-card {
    padding: 1.5rem;
  }
  
  .form-title {
    font-size: 1.75rem;
  }
  
  .radio-group {
    gap: 1rem;
  }
}
</style>